<template>
  <nav class="navwrap" ref="navwrap">
    <!-- Mobile hamburger -->
    <button
      class="nav-toggle"
      v-show="!isDesktop"
      @click="toggleMainMenu"
      :aria-expanded="isMenuOpen.toString()"
      aria-controls="navigation"
      aria-label="Menú"
    >
      ☰
    </button>

    <!-- Desktop: always visible; Mobile: controlled by .menu-open class -->
    <ul id="navigation" class="sf-menu" :class="{ 'menu-open': isMenuOpen, 'desktop-mode': isDesktop }">
      <!-- Babilonia (has submenu) -->
      <li class="has-sub">
        <div class="toprow">
          <!-- Border lives on the ANCHOR (same as others).
               Caret is rendered via ::after (so it doesn't change size). -->
          <a
            href="#"
            class="toplink"
            role="button"
            tabindex="0"
            :aria-expanded="(!isDesktop && isSubOpen).toString()"
            @click.prevent.stop="toggleSubMobile"
            @keydown.enter.prevent.stop="toggleSubMobile"
            @keydown.space.prevent.stop="toggleSubMobile"
          >Babilonia</a>
        </div>

        <!-- Submenu BELOW on mobile; hover on desktop -->
        <ul class="submenu" :class="{ 'is-open': (!isDesktop && isSubOpen) }">
          <li><router-link to="/el-grupo#el-grupo">El Grupo</router-link></li>
          <li><router-link to="/el-grupo#quien-somos">Quién Somos</router-link></li>
          <li><router-link to="/el-grupo#historia">Historia</router-link></li>
          <li><router-link to="/el-grupo#premios">Premios</router-link></li>
        </ul>
      </li>

      <!-- Cartelera -->
      <li><router-link to="/cartelera">Cartelera</router-link></li>

      <!-- Galería -->
      <li><router-link to="/galeria">Galería</router-link></li>

      <!-- Talleres -->
      <li><router-link to="/talleres/anual">Talleres</router-link></li>

      <li><router-link to="/obras">Obras</router-link></li>

      <!-- Auth link -->
      <li v-if="isLoggedIn">
        <router-link to="/mi-cuenta" class="toplink auth-link">
          👤 {{ userName }}
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import $ from "jquery";
import "superfish/dist/css/superfish.css";

export default {
  name: "NavBar",
  setup() {
    const route = useRoute();
    const isDesktop   = ref(false);
    const isMenuOpen  = ref(false);
    const isSubOpen   = ref(false);
    const navwrap     = ref(null);
    let sfEnabled = false;

    const enableSuperfish = async () => {
      if (sfEnabled) return;
      try {
        window.$ = window.jQuery = $; // UMD plugin needs globals
        await import("superfish/dist/js/superfish.min.js");
        $("#navigation").superfish({
          delay: 200,
          animation: { opacity: "show", height: "show" },
          speed: "fast",
          cssArrows: false
        });
        sfEnabled = true;
      } catch (e) {
        isSubOpen.value = false;
      }
    };

    const destroySuperfish = () => {
      if (!sfEnabled) return;
      try { $("#navigation").superfish("destroy"); }
      catch (e) { isSubOpen.value = false; }
      sfEnabled = false;
    };

    const applyMode = () => {
      // Match CSS breakpoint
      isDesktop.value = window.innerWidth > 767;
      if (isDesktop.value) {
        isMenuOpen.value = true;   // show bar on desktop
        isSubOpen.value  = false;
        enableSuperfish();
      } else {
        destroySuperfish();
        isMenuOpen.value = false;  // collapsed on mobile
        isSubOpen.value  = false;
      }
    };

    const toggleMainMenu = () => {
      isMenuOpen.value = !isMenuOpen.value;
      if (!isMenuOpen.value) {
        isSubOpen.value = false; // close submenu when main closes
      }
    };

    const toggleSubMobile = () => {
      if (isDesktop.value) return; // desktop uses hover
      isSubOpen.value = !isSubOpen.value;
    };


    // Close when clicking outside (mobile)
    const handleOutside = (e) => {
      if (isDesktop.value) return;
      if (!navwrap.value) return;
      if (!navwrap.value.contains(e.target)) {
        isMenuOpen.value = false;
        isSubOpen.value = false;
      }
    };

    const onResize = () => applyMode();

    onMounted(() => {
      applyMode();
      window.addEventListener("resize", onResize);
      document.addEventListener("mousedown", handleOutside);
      document.addEventListener("touchstart", handleOutside, { passive: true });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      destroySuperfish();
    });

    // Close all menus when route changes (crucial for mobile submenu navigation)
    watch(() => route.fullPath, () => {
      if (!isDesktop.value) {
        isMenuOpen.value = false;
        isSubOpen.value = false;
      }
    });

    // Auth state
    const authStore = ref(null)
    const isLoggedIn = computed(() => authStore.value?.isLoggedIn || false)
    const userName = computed(() => {
      const user = authStore.value?.currentUser
      if (!user) return ''
      return user.first_name || user.email.split('@')[0]
    })

    onMounted(async () => {
      const { authStore: store } = await import('@/store/auth.js')
      authStore.value = store
    })

    return {
      isDesktop, isMenuOpen, isSubOpen,
      navwrap,
      toggleMainMenu, toggleSubMobile,
      isLoggedIn, userName
    };
  }
};
</script>

<style>
/* ===== Auth link ===== */
.auth-link {
  color: #c9a227 !important;
  font-size: 13px !important;
}

/* ===== Desktop/base ===== */
.navwrap { background: none; padding: 0; margin: 0; overflow: visible; }

#navigation {
  list-style: none;
  padding: 6px 10px;
  margin: 0;
  display: flex;
  gap: 12px;
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: visible;
}

#navigation.desktop-mode {
  display: flex;
}

#navigation li { position: relative; }

#navigation li a {
  font-weight: bold;
  color: red;
  padding: 10px 15px;
  text-decoration: none;
  font-size: 18px;
  display: block;
  border: 1px solid white;
  border-radius: 5px;
}

/* Toprow wrapper for Babilonia */
#navigation li .toprow {
  display: block;
}

#navigation li .toprow .toplink {
  font-weight: bold;
  color: red;
  padding: 10px 15px;
  text-decoration: none;
  font-size: 18px;
  display: block;
  border: 1px solid white;
  border-radius: 5px;
}

/* Desktop dropdown (Superfish hover) */
#navigation li.has-sub {
  position: relative;
}

#navigation ul.submenu {
  position: absolute;
  left: 0;
  top: 100%;
  background: white;
  display: none;
  list-style: none;
  padding: 0;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  min-width: 200px;
  border-radius: 5px;
  z-index: 1000;
  text-align: left;
}

/* Show submenu on hover for desktop and tablet */
@media (min-width: 768px) {
  #navigation li.has-sub:hover > ul.submenu { display: block !important; }
}

#navigation ul.submenu li { width: 200px; text-align: left; }
#navigation ul.submenu li:hover { background: red !important; }
#navigation ul.submenu li a { color: black; padding: 10px; display: block; border: none; text-align: left; }
#navigation ul.submenu li:hover > a { background: red !important; color: #fff !important; }

.sf-menu li { background: none !important; margin: 0 !important; }

/* Hide toggles on desktop and tablet */
.nav-toggle { display: none; }

/* ===== Tablet (768px - 992px) ===== */
@media (min-width: 768px) and (max-width: 992px) {
  #navigation {
    gap: 6px !important;
  }

  #navigation li a {
    padding: 6px 8px !important;
    font-size: 13px !important;
  }

  #navigation li .toprow .toplink {
    padding: 6px 8px !important;
    font-size: 13px !important;
  }
}

/* ===== Mobile-only (below 768px) ===== */
@media (max-width: 767px){
  /* compact uniform sizing */
  .navwrap{
    --item-h: 42px;   /* uniform height */
    --pad-x: 15px;    /* your original */
    --caret-w: 22px;  /* visual caret width */
    --caret-gap: 6px; /* space between text and caret */
  }

  /* Hamburger on left */
  .navwrap{
    position: relative;
    display: block;
    z-index: 3000;
    text-align: left !important;
  }

  .nav-toggle{
    display: inline-block !important;
    margin: 0;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    color: #fff;
    border: none;
    border-radius: 0;
    padding: 6px;
    font-size: 24px;
    cursor: pointer;
  }

  /* Panel under the button — hidden by default on mobile */
  #navigation {
    display: none !important;
    position: absolute;
    left: 0;
    top: calc(100% + 6px);
    width: 280px;
    z-index: 2999;
    flex-direction: column;
    gap: 10px;
    background: #000 !important;
    border: 1px solid #444;
    border-radius: 10px;
    padding: 10px;
    text-align: left !important;
  }

  /* Show menu ONLY when menu-open class is present */
  #navigation.menu-open {
    display: flex !important;
  }

  /* Make every top-level item full width and aligned */
  #navigation > li{
    width: 100%;
    margin: 0 !important;
    padding: 0 !important;
    text-align: left !important;
  }

  /* Other top-level items — SAME height; borders unchanged */
  #navigation > li:not(.has-sub) > a,
  #navigation > li:not(.has-sub) > a{
    display: flex;
    align-items: center;
    justify-content: flex-start;         /* left align text */
    height: var(--item-h);
    padding: 0 var(--pad-x);
    margin: 0;
    box-sizing: border-box;
    text-align: left !important;

    border: 1px solid #fff;
    border-radius: 6px;
    color: red !important;
    background: transparent;
  }

  /* Ensure has-sub li doesn't get extra margin from submenu */
  #navigation > li.has-sub{
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Babilonia row — SAME height; caret drawn via ::after */
  #navigation > li.has-sub{ width: 100%; }
  .toprow{
    width: 100%;
    margin: 0;
    padding: 0;
    text-align: left !important;
  }
  .toprow .toplink{
    position: relative;                  /* for caret */
    display: flex;
    align-items: center;
    justify-content: flex-start;         /* left align text */
    height: var(--item-h);
    width: 100%;
    box-sizing: border-box;

    border: 1px solid #fff;
    border-radius: 6px;
    color: red !important;
    background: transparent;
    text-decoration: none;
    text-align: left !important;

    padding: 0 var(--pad-x);             /* same left/right as others */
  }
  .toprow .toplink::after{
    content: "▸";
    position: absolute;
    right: var(--pad-x);
    top: 50%;
    transform: translateY(-50%) rotate(0deg);
    color: #fff;
    width: var(--caret-w);
    text-align: center;
  }
  /* rotate when open */
  .toprow .toplink[aria-expanded="true"]::after{
    transform: translateY(-50%) rotate(90deg);
  }

  /* Submenu BELOW (accordion style) */
  .submenu,
  #navigation ul.submenu,
  #navigation li.has-sub ul.submenu {
    display: none !important;
    position: static !important;
    width: 100%;
    margin: 8px 0 0 0;
    padding: 6px 0;
    background: #000 !important;
    border: 1px solid #444;
    border-radius: 6px;
    box-shadow: none !important;
    list-style: none;
    text-align: left !important;
  }

  .submenu li,
  #navigation ul.submenu li {
    text-align: left !important;
  }

  /* Neutralize any hover-open on mobile */
  #navigation li.has-sub:hover > ul.submenu { display: none !important; }

  /* Show submenu ONLY via is-open class on mobile */
  .submenu.is-open,
  #navigation ul.submenu.is-open,
  #navigation li.has-sub > ul.submenu.is-open {
    display: block !important;
  }

  /* Submenu links - override desktop black color */
  #navigation ul.submenu li a,
  #navigation.sf-menu ul.submenu li a,
  #navigation li.has-sub ul.submenu li a,
  .submenu li a {
    color: #fff !important;
    padding: 10px 14px !important;
    border: 0 !important;
    background: #000 !important;
    display: block !important;
    text-align: left !important;
  }
  #navigation ul.submenu li:hover,
  #navigation ul.submenu li:hover > a,
  .submenu li:hover,
  .submenu li:hover > a {
    background: red !important;
    color: #fff !important;
  }
}
</style>